// Логиката на редовете на листа „Изписване": състоянието и операциите —
// добавяне от търсачката и от „Целия списък" (с въведеното количество, без
// дубликат — `writeoffCatalogLines`), промяна/премахване, разделяне на
// рецепта на продукти и връщане обратно, наличност/партиди КЪМ ИЗБРАНАТА ДАТА
// (as-of, `useWriteoffStock` — и за съставките на рецептите), проверката „не
// стига" за целия документ и живите суми Нето/Бруто (продукти + рецепти).
//
// Всяко добавяне от търсачката прави НОВ ред, дори продуктът вече да е в
// листа: партидата (FIFO или конкретна) е на ниво ред.
import { useMemo, useRef, useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { Product, RecipeIngredient } from "../../../../api/inventory";
import { clampDecimals } from "../../components/qty";
import { useConfirm } from "../../components/confirm/useConfirm";
import {
  removeRowConfirm,
  revertGroupConfirm,
  splitPortionsOf,
  splitRecipeConfirm,
  splitRecipePlan,
} from "./writeoffConfirms";
import { grossOfNet } from "./writeoffStockCheck";
import { recipeIngredientsQuery, useWriteoffStock } from "./useWriteoffStock";
import { planCatalogLines, type WriteoffCatalogPick } from "./writeoffCatalogLines";
import {
  emptyWriteoffLine,
  fifoUnitPrice,
  formatPortionsInput,
  formatQtyInput,
  parseDecimal,
  type WriteoffLineDraft,
  type WriteoffSearchResult,
} from "./writeoffWorksheetShared";

type Params = {
  initialLines: WriteoffLineDraft[];
  initialNextKey: number;
  warehouseId: number | null;
  asOfDayKey: string | null; // YYYY-MM-DD на избраната дата (null = без дата)
  // редактираното изписване — наличността се пита без него (както я сверява BE)
  excludeMovementId: number | null;
  // автоматичен брак от Маркиране — без проверка за наличност (и на BE)
  skipStockCheck: boolean;
  vatRegistered: boolean;
  productById: Map<number, Product>;
  onTouch: () => void; // вдига флага „незапазени промени"
  focusQtyCell: (lineKey: number) => void;
};

// „Раздели на продукти": с въпрос (бутонът на реда) или направо (изборът в
// диалога „Не стига наличност" — там въпросът вече е зададен)
type SplitOptions = { askFirst?: boolean };

export function useWriteoffRowsActions({
  initialLines,
  initialNextKey,
  warehouseId,
  asOfDayKey,
  excludeMovementId,
  skipStockCheck,
  vatRegistered,
  productById,
  onTouch,
  focusQtyCell,
}: Params) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [lines, setLines] = useState<WriteoffLineDraft[]>(initialLines);
  const lineKeyCounter = useRef(initialNextKey);

  // ─── Наличност към датата, недостиг и стойността на рецептите ────────────
  const { asOfByProduct, shortages, overProductIds, recipeStockByKey } = useWriteoffStock({
    lines,
    warehouseId,
    asOfDayKey,
    excludeMovementId,
    skipStockCheck,
    productById,
    vatRegistered,
  });

  // ─── Суми на живо (Нето/Бруто): продуктовите редове по партидите/FIFO,
  // рецептите — по същата основа за съставките си ─────────────────────────
  const lineNetValue = (line: WriteoffLineDraft): number => {
    if (line.kind === "recipe") return recipeStockByKey.get(line.key)?.values.netSum ?? 0;
    if (line.productId == null) return 0;
    const quantity = parseDecimal(line.quantity);
    if (quantity <= 0) return 0;
    const lots = asOfByProduct.get(line.productId)?.lots ?? [];
    const unitPrice = fifoUnitPrice(lots, quantity, line.lotId);
    return unitPrice == null ? 0 : quantity * unitPrice;
  };
  const lineGrossValue = (line: WriteoffLineDraft): number => {
    if (line.kind === "recipe") return recipeStockByKey.get(line.key)?.values.grossSum ?? 0;
    return grossOfNet(lineNetValue(line), productById.get(line.productId as number)?.vatRate, vatRegistered);
  };
  const totalNet = lines.reduce((sum, line) => sum + lineNetValue(line), 0);
  const totalGross = lines.reduce((sum, line) => sum + lineGrossValue(line), 0);

  const validLines = lines.filter((line) =>
    line.kind === "recipe"
      ? line.recipeId && parseDecimal(line.quantity) > 0
      : line.productId && parseDecimal(line.quantity) > 0,
  );

  // Сумарни количества по мерни единици (чиповете в заглавието на картата, както
  // при доставка) + общ брой порции от редовете-рецепти.
  const unitTotals = useMemo(() => {
    const totalsByUnit = new Map<string, { qty: number; dec: number }>();
    let portions = 0;
    for (const line of lines) {
      const qty = parseDecimal(line.quantity);
      if (qty <= 0) continue;
      if (line.kind === "recipe") {
        portions += qty;
        continue;
      }
      if (line.productId == null) continue;
      const product = productById.get(line.productId);
      const unit = product?.unit?.trim() || "—";
      const current = totalsByUnit.get(unit);
      if (current) current.qty += qty;
      else totalsByUnit.set(unit, { qty, dec: clampDecimals(product?.decimalPlaces) });
    }
    return {
      units: [...totalsByUnit.entries()].sort((a, b) => b[1].qty - a[1].qty),
      portions,
    };
  }, [lines, productById]);

  // ─── Операции върху редовете ─────────────────────────────────────────────
  const patchLine = (key: number, patch: Partial<WriteoffLineDraft>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    onTouch();
  };
  // „Намали на N порции" — от лентата „Не стига" и от диалога при запис
  const setRecipePortions = (key: number, portions: number) =>
    patchLine(key, { quantity: formatPortionsInput(portions) });

  const lineOf = (result: WriteoffSearchResult, key: number): WriteoffLineDraft =>
    result.kind === "product"
      ? { ...emptyWriteoffLine(key, "product"), productId: result.id }
      : { ...emptyWriteoffLine(key, "recipe"), recipeId: result.id, recipeName: result.name };

  // Нови редове накрая; фокусът — в „Количество" на първия добавен
  const addLines = (results: WriteoffSearchResult[]) => {
    if (!results.length) return;
    const newLines = results.map((result) => lineOf(result, lineKeyCounter.current++));
    setLines((prev) => [...prev, ...newLines]);
    onTouch();
    focusQtyCell(newLines[0].key);
  };
  const addFromSearch = (result: WriteoffSearchResult) => addLines([result]);

  // „Целият списък" с въведеното: без дубликат (`writeoffCatalogLines`) —
  // вписаният ред получава количеството си, новите са накрая; фокусът — в
  // „Количество" на първия избран
  const addCatalogLines = (picks: WriteoffCatalogPick[]) => {
    if (!picks.length) return;
    const { fills, fresh, firstExistingKey } = planCatalogLines(lines, picks);
    const newLines = fresh.map((pick) => ({
      ...lineOf(pick.result, lineKeyCounter.current++),
      quantity: pick.quantity ?? "",
    }));
    setLines((prev) => [
      ...prev.map((line) => {
        const quantity = fills.get(line.key);
        return quantity == null ? line : { ...line, quantity };
      }),
      ...newLines,
    ]);
    onTouch();
    const focusKey = firstExistingKey ?? newLines[0]?.key;
    if (focusKey != null) focusQtyCell(focusKey);
  };

  // Въпросите (components/confirm): името на реда, количеството и прегледа
  // на последствията — текстовете са в writeoffConfirms.
  const removeLine = (key: number) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
    onTouch();
  };
  const requestRemove = async (key: number) => {
    const line = lines.find((candidate) => candidate.key === key);
    if (!line) return;
    if (await confirm(removeRowConfirm(t, line, productById))) removeLine(key);
  };

  // „Раздели на продукти" → заменя ред-рецепта с продуктовите ѝ редове
  // (количество = съставка × порции). Запазва recipe-данните за връщане.
  // Съставките се четат ПРЕДИ въпроса (от кеша на листа, ако вече са там) —
  // той показва точно редовете, които ще се появят; при празна рецепта/грешка
  // въпрос изобщо няма. Връща новите редове (null — нищо не е разделено).
  const splittingRef = useRef(false);
  const splitRecipeRow = async (
    key: number,
    { askFirst = true }: SplitOptions = {},
  ): Promise<WriteoffLineDraft[] | null> => {
    const row = lines.find((line) => line.key === key);
    if (!row || !row.recipeId) {
      message.warning(t("inventoryPage.selectRecipe"));
      return null;
    }
    if (splittingRef.current) return null;
    splittingRef.current = true;
    const recipeId = row.recipeId;
    const { portions, defaulted } = splitPortionsOf(row);
    try {
      let ingredients: RecipeIngredient[];
      try {
        ingredients = await queryClient.fetchQuery(recipeIngredientsQuery(recipeId));
      } catch {
        message.error(t("inventoryPage.errRecipe"));
        return null;
      }
      if (ingredients.length === 0) {
        message.warning(t("inventoryPage.recipeEmpty"));
        return null;
      }
      const plan = splitRecipePlan(ingredients, portions, productById);
      if (askFirst) {
        const confirmed = await confirm(
          splitRecipeConfirm(t, {
            recipeName: row.recipeName ?? "",
            portions,
            portionsDefaulted: defaulted,
            plan,
          }),
        );
        if (!confirmed) return null;
      }
      const groupId = lineKeyCounter.current++;
      const productRows: WriteoffLineDraft[] = plan.map((planLine) => ({
        ...emptyWriteoffLine(lineKeyCounter.current++, "product"),
        productId: planLine.productId,
        quantity: planLine.quantity != null ? formatQtyInput(planLine.quantity, planLine.decimals) : "",
        groupId,
        groupRecipeId: recipeId,
        groupRecipeName: row.recipeName,
        groupPortions: portions,
        groupNote: row.note,
      }));
      setLines((prev) => {
        const index = prev.findIndex((line) => line.key === key);
        if (index === -1) return [...prev, ...productRows];
        const copy = [...prev];
        copy.splice(index, 1, ...productRows);
        return copy;
      });
      onTouch();
      return productRows;
    } finally {
      splittingRef.current = false;
    }
  };

  // „Върни в рецепта" → събира продуктовите редове на групата обратно в един
  // ред-рецепта (на първата им позиция).
  const revertGroup = (groupId: number) => {
    setLines((prev) => {
      const groupRow = prev.find((line) => line.groupId === groupId);
      if (!groupRow) return prev;
      const recipeRow: WriteoffLineDraft = {
        ...emptyWriteoffLine(lineKeyCounter.current++, "recipe"),
        recipeId: groupRow.groupRecipeId,
        recipeName: groupRow.groupRecipeName,
        quantity: groupRow.groupPortions != null ? formatPortionsInput(groupRow.groupPortions) : "",
        note: groupRow.groupNote ?? "",
      };
      const out: WriteoffLineDraft[] = [];
      let inserted = false;
      for (const line of prev) {
        if (line.groupId === groupId) {
          if (!inserted) {
            out.push(recipeRow);
            inserted = true;
          }
        } else {
          out.push(line);
        }
      }
      return out;
    });
    onTouch();
  };
  const requestRevert = async (groupId: number) => {
    const groupLines = lines.filter((line) => line.groupId === groupId);
    if (groupLines.length === 0) return;
    if (await confirm(revertGroupConfirm(t, groupLines, productById))) revertGroup(groupId);
  };

  return {
    lines,
    asOfByProduct,
    overProductIds,
    shortages,
    recipeStockByKey,
    totalNet,
    totalGross,
    validLines,
    unitTotals,
    patchLine,
    setRecipePortions,
    addFromSearch,
    addLines,
    addCatalogLines,
    requestRemove,
    splitRecipeRow,
    requestRevert,
  };
}
